/**
 * The BuzzBrain app report's tabs — the same six as the science site's, in the
 * same order, so the two reports read as one kind of thing (AppUsageReport.tsx
 * holds the filter bar and the tab strip).
 */
import { useMemo, useState } from 'react';
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { DataTable, InfoTip, type ColumnDef, type SortState } from '@shared-ui/components';
import type { AppUsage } from '../../../api/appUsageApi';
import type { SitePathNode } from '../../../api/siteAnalyticsApi';
import PathSunburst, { type SunburstVocab } from '../shared/PathSunburst';
import { Table } from '../shared/RankedTable';
import { facetOptions, sectionOf, type RankedRow } from '../shared/rankedRows';
import { firstTouchLabel } from '../shared/sourceLabels';
import { n, pct } from './format';


function PracticeTrend({ points }: { points: AppUsage['trend'] }) {
  if (points.length < 2) return <p className="sa-empty">Not enough days in this window to draw a trend.</p>;
  const data = points.map(p => ({
    ...p,
    label: new Date(`${p.date}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' }),
  }));
  return (
    <div className="sa-trend">
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
          <CartesianGrid stroke="var(--border-color)" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} minTickGap={24} />
          <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} allowDecimals={false} />
          <Tooltip contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line type="monotone" dataKey="people" name="Students" stroke="#10b981" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="answers" name="Answers" stroke="#f59e0b" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="hints" name="Hints" stroke="#6366f1" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

type LinkRow = AppUsage['links'][number];
type LandingRow = AppUsage['sessions']['landings'][number];

const num = <T,>(key: keyof T & string, header: string, tip: string): ColumnDef<T> => ({
  key, header: <span title={tip}>{header}</span>, sortable: true, className: 'sa-num',
  sortValue: r => r[key] as number,
  render: r => n(r[key] as number),
});

/** A count with its share of a row's base beside it. */
const share = <T,>(key: keyof T & string, header: string, tip: string, of: (r: T) => number): ColumnDef<T> => ({
  key, header: <span title={tip}>{header}</span>, sortable: true, className: 'sa-num',
  sortValue: r => r[key] as number,
  render: r => <>{n(r[key] as number)} <span className="sa-row-sub">{pct(r[key] as number, of(r))}</span></>,
});

/** The sessions caveat, said where a tab is built from sessions. */
function SessionsNote({ data }: { data: AppUsage }) {
  const s = data.sessions;
  return (
    <span className="sa-tip-p">
      From recorded sessions, which exist only while session tracing is on — {n(s.peopleWithSessions)} of the{' '}
      {n(data.totals.people)} students in this report ({pct(s.peopleWithSessions, data.totals.people)}), over{' '}
      {n(s.sessions)} sessions. The Overview and What students did count every student.
    </span>
  );
}

export function Overview({ data }: { data: AppUsage }) {
  const t = data.totals;
  return (
    <>
      <div className="sa-stats">
        {([
          ['Students', t.people, 'active in the app',
            'Students who used the app in this window — guests included, teachers, testers and science-site readers who did nothing in the app left out.'],
          ['Active days', t.activeDays, 'student-days',
            'Each student counted once per day they used the app. One student on three days is 3.'],
          ['Answers submitted', t.answers, 'answers',
            'Every answer a student submitted — typed, chosen or handwritten. Recorded server-side, so complete.'],
          ['Hints used', t.hints, 'hints', 'Every hint a student asked for.'],
          ['Solutions viewed', t.solutionViews, 'views', 'Each time a student opened the solution analysis for a question.'],
        ] as const).map(([label, value, note, definition]) => (
          <div className="sa-stat" key={label}>
            <span className="sa-stat-value">{n(value)}</span>
            <span className="sa-stat-label">{label}<InfoTip text={definition} /></span>
            <span className="sa-stat-note">{note}</span>
          </div>
        ))}
      </div>
      <section className="sa-panel">
        <h2>Per day</h2>
        <PracticeTrend points={data.trend} />
      </section>
    </>
  );
}

type ScreenRow = NonNullable<AppUsage['screens']>[number];
type SourceRow = NonNullable<AppUsage['sources']>[number];


export function Tables({ data }: { data: AppUsage }) {
  const [sourceSort, setSourceSort] = useState<SortState>({ sortBy: 'students', sortOrder: 'desc' });
  // The science site's table, with the same search and section filter: a
  // screen is a route template (`/session/:id`), its section the first part.
  const screens: RankedRow[] = (data.screens ?? []).map(r => ({
    key: r.path, label: r.path, users: r.users, visits: r.sessions, events: r.views,
    search: r.path.toLowerCase(), facets: { section: [sectionOf(r.path)] },
  }));
  const sourceColumns: ColumnDef<SourceRow>[] = [
    { key: 'source', header: 'First source', sortable: true, sortValue: r => r.source, render: r => <span className="sa-label">{firstTouchLabel(r.source)}</span> },
    num<SourceRow>('students', 'Students', 'Students active in the window whose first touch was this.'),
    share<SourceRow>('answered', 'Answered', 'Of those, submitted at least one answer.', r => r.students),
    share<SourceRow>('cameBack', 'Came back', 'Of those, active on two or more days.', r => r.students),
    share<SourceRow>('signedUp', 'Has an account', 'Of those, not a guest.', r => r.students),
  ];
  return (
    <>
      <div className="sa-grid">
        <Table
          title="Most-used screens"
          labelHead="Screen"
          rows={screens}
          filters={[{ key: 'section', kind: 'select', placeholder: 'All sections', options: facetOptions(screens, 'section') }]}
        />
        <section className="sa-panel">
          <h2>
            Where students came from
            <InfoTip text="Every student active in the window, by their account’s first touch — shown as “Mapped name (actual value)” — and what they went on to do. Counted from the complete daily records, not sessions." />
          </h2>
          <DataTable<SourceRow> columns={sourceColumns} data={data.sources ?? []} rowKey={r => r.source}
            sort={sourceSort} onSortChange={setSourceSort} compact emptyMessage="No students in this window." />
        </section>
      </div>
      <ByLink data={data} />
      {data.screens && <p className="sa-note"><SessionsNote data={data} /></p>}
    </>
  );
}

export function WhatStudentsDid({ data }: { data: AppUsage }) {
  const p = data.practice;
  const measures: [string, number, string][] = [
    ['Answered a question', p.answered, 'Submitted at least one answer in the window.'],
    ['Answered on 2+ days', p.answeredOn2Days, 'Submitted answers on two or more different days — practice that came back.'],
    ['Came back on another day', p.cameBack, 'Active on two or more different days.'],
    ['Viewed a solution', p.viewedSolution, 'Opened the solution analysis for a question.'],
    ['Used a hint', p.usedHint, 'Asked for at least one hint.'],
    ['Has an account', p.signedUp, 'Not a guest — signed up, or an account a teacher or admin made.'],
  ];
  return (
    <section className="sa-panel">
      <h2>
        What students did
        <InfoTip>
          <span className="sa-tip-p">
            Each row is a share of the {n(p.active)} students active in the window — independent measures, not
            a funnel: a student can use a hint without answering.
          </span>
        </InfoTip>
      </h2>
      <table className="sa-table">
        <thead><tr><th>Measure</th><th className="sa-num">Students</th><th className="sa-num">Of active</th></tr></thead>
        <tbody>
          {measures.map(([name, value, tip]) => (
            <tr key={name}>
              <td className="sa-label" title={tip}>{name}</td>
              <td className="sa-num">{n(value)}</td>
              <td className="sa-num">{pct(value, p.active)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="sa-bars">
        {measures.map(([name, value]) => (
          <div className="sa-bar-row" key={name}>
            <span className="sa-bar-label">{name}</span>
            <span className="sa-bar-track">
              <span className="sa-bar-fill" style={{ width: `${p.active ? (value / p.active) * 100 : 0}%`, background: '#6366f1' }} />
            </span>
            <span className="sa-bar-pct">{pct(value, p.active)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function ByLink({ data }: { data: AppUsage }) {
  const [sort, setSort] = useState<SortState>({ sortBy: 'opens', sortOrder: 'desc' });
  const columns: ColumnDef<LinkRow>[] = [
    { key: 'slug', header: 'Link', sortable: true, sortValue: r => r.slug, render: r => <span className="sa-label">/p/{r.slug}</span> },
    num<LinkRow>('opens', 'Opens', 'Follows of the link recorded in the window.'),
    num<LinkRow>('accounts', 'Accounts', 'Guests the link created in the window.'),
    num<LinkRow>('active', 'Active', 'Of those accounts, active in the app in the window.'),
    num<LinkRow>('answered', 'Answered', 'Of those accounts, submitted at least one answer.'),
    num<LinkRow>('cameBack', 'Came back', 'Of those accounts, active on two or more days.'),
    num<LinkRow>('signedUp', 'Signed up', 'Of those accounts, no longer a guest.'),
  ];
  return (
    <section className="sa-panel">
      <h2>
        By published link
        <InfoTip>
          <span className="sa-tip-p">
            Each link’s opens, and the guests it created in the window with what they went on to do. Until the app
            ties a follow to the session after it, only people new through a link are counted here.
          </span>
        </InfoTip>
      </h2>
      <DataTable<LinkRow> columns={columns} data={data.links} rowKey={r => r.slug}
        sort={sort} onSortChange={setSort} compact emptyMessage="No published link was followed or made an account in this window." />
    </section>
  );
}

export function Landings({ data }: { data: AppUsage }) {
  const [sort, setSort] = useState<SortState>({ sortBy: 'sessions', sortOrder: 'desc' });
  const columns: ColumnDef<LandingRow>[] = [
    { key: 'landing', header: 'Landing screen', sortable: true, sortValue: r => r.landing, render: r => <span className="sa-label">{r.landing}</span> },
    num<LandingRow>('sessions', 'Sessions', 'Sessions that began on this screen.'),
    num<LandingRow>('people', 'Students', 'Distinct students with a session that began here.'),
    num<LandingRow>('answers', 'Answers', 'Answers submitted in those sessions.'),
  ];
  return (
    <section className="sa-panel">
      <h2>
        Where sessions began
        <InfoTip><SessionsNote data={data} /></InfoTip>
      </h2>
      <DataTable<LandingRow> columns={columns} data={data.sessions.landings} rowKey={r => r.landing}
        sort={sort} onSortChange={setSort} compact emptyMessage="No sessions recorded in this window." />
    </section>
  );
}

const SCREEN_VOCAB: SunburstVocab = {
  nameOf: path => (path.length === 0 ? 'All screens' : path[0] === '/' ? '/' : `/${path.join('/')}`),
  sectionHeader: 'Section',
  childNoun: 'screens',
  sizeLabel: 'Views',
  sizeUnit: 'views',
  countLabel: 'Sessions',
  countDetail: 'Sessions',
  hint: where => (
    <>
      Inner ring: the sections of {where === 'the site' ? 'the app' : where}. Outer ring: the screens inside each
      one. Each slice is sized by its share of views. Hover a slice for its numbers; click one with screens under it
      to open it.
    </>
  ),
};

/** Screens as a tree, split on '/', so a whole section can be weighed. */
function screenTree(rows: ScreenRow[]): SitePathNode {
  const root: SitePathNode = { name: 'app', visits: 0, events: 0, children: [] };
  for (const r of rows) {
    const parts = r.path.split('/').filter(Boolean);
    let node = root;
    root.visits += r.sessions; root.events += r.views;
    for (const part of parts.length ? parts : ['/']) {
      let child = node.children.find(c => c.name === part);
      if (!child) { child = { name: part, visits: 0, events: 0, children: [] }; node.children.push(child); }
      child.visits += r.sessions; child.events += r.views;
      node = child;
    }
  }
  const sort = (n: SitePathNode) => { n.children.sort((a, b) => b.events - a.events); n.children.forEach(sort); };
  sort(root);
  return root;
}

export function Paths({ data }: { data: AppUsage }) {
  const tree = useMemo(() => screenTree(data.screens ?? []), [data.screens]);
  return (
    <section className="sa-panel">
      <h2>
        Where practice went
        <InfoTip><SessionsNote data={data} /></InfoTip>
      </h2>
      {tree.children.length === 0
        ? <p className="sa-empty">No screens recorded in this window.</p>
        : <PathSunburst root={tree} vocab={SCREEN_VOCAB} />}
    </section>
  );
}
